import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { TaskSortType } from '@/components/Task/types'
import { configService } from '@/services/configService'

export type Layout = "list" | "grid" | "horizontal"
export type ViewType = "task" | "planner"
export type PlannerViewCalendarView = "dayGridMonth" | "timeGridWeek" | "timeGridDay"

export interface SeenFeature {
  firstSeenAt: string
  tourDismissedAt?: string
}

export interface UserConfig {
  layout: Layout
  activeView: ViewType
  showCalendarSidebar: boolean
  plannerViewCalendarView: PlannerViewCalendarView
  accounts: {
    [accountId: string]: {
      listOrder: string[]
      nickname?: string
    }
  }
  tasklists: {
    [accountId: string]: {
      [tasklistId: string]: {
        sortType: TaskSortType
      }
    }
  }
  seenFeatures: {
    [featureKey: string]: SeenFeature
  }
}

const defaultConfig: UserConfig = {
  layout: "grid",
  activeView: "task",
  showCalendarSidebar: false,
  plannerViewCalendarView: "dayGridMonth",
  accounts: {},
  tasklists: {},
  seenFeatures: {}
}

// Load config from localStorage synchronously on startup for instant restore
const getInitialConfig = (): UserConfig => {
  const stored = configService.getFromLocalStorage()
  if (stored) {
    return { ...defaultConfig, ...stored }
  }
  return defaultConfig
}

interface ConfigState {
  config: UserConfig
  isLoaded: boolean
  setConfig: (config: UserConfig) => void
  setLayout: (layout: Layout) => void
  setActiveView: (activeView: ViewType) => void
  setShowCalendarSidebar: (showCalendarSidebar: boolean) => void
  setPlannerViewCalendarView: (view: PlannerViewCalendarView) => void
  setListOrder: (accountId: string, listOrder: string[]) => void
  setAccountNickname: (accountId: string, nickname: string | undefined) => void
  setTasklistSortType: (accountId: string, tasklistId: string, sortType: TaskSortType) => void
  cleanupAccount: (accountId: string) => void
  cleanupTasklist: (accountId: string, tasklistId: string) => void
  markFeatureSeen: (featureKey: string) => void
  resetFeatureSeen: (featureKey: string) => void
  getConfig: () => UserConfig
  reset: () => void
}

let syncTimeout: NodeJS.Timeout | null = null
const SYNC_DELAY = 1500

const debouncedFirestoreSync = (config: UserConfig, isLoaded: boolean) => {
  if (!isLoaded) return
  
  if (syncTimeout) {
    clearTimeout(syncTimeout)
  }
  
  syncTimeout = setTimeout(async () => {
    try {
      configService.saveToLocalStorage(config)
      await configService.saveToFirestore(config)
    } catch (error) {
      console.warn('Failed to sync config to Firestore:', error)
    }
  }, SYNC_DELAY)
}

export const useConfigStore = create<ConfigState>()(
  devtools(
    (set, get) => ({
      config: getInitialConfig(),
      isLoaded: false,
      setConfig: (config) => set({ config, isLoaded: true }, false, 'setConfig'),
      setLayout: (layout) => set(
        (state) => {
          return ({
          config: { ...state.config, layout }
        })},
        false,
        'setLayout'
      ),
      setActiveView: (activeView) => set(
        (state) => ({
          config: { ...state.config, activeView }
        }),
        false,
        'setActiveView'
      ),
      setShowCalendarSidebar: (showCalendarSidebar) => set(
        (state) => ({
          config: { ...state.config, showCalendarSidebar }
        }),
        false,
        'setShowCalendarSidebar'
      ),
      setPlannerViewCalendarView: (plannerViewCalendarView) => set(
        (state) => ({
          config: { ...state.config, plannerViewCalendarView }
        }),
        false,
        'setPlannerViewCalendarView'
      ),
      setListOrder: (accountId, listOrder) => set(
        (state) => ({
          config: {
            ...state.config,
            accounts: {
              ...state.config.accounts,
              [accountId]: {
                ...state.config.accounts[accountId],
                listOrder
              }
            }
          }
        }),
        false,
        'setListOrder'
      ),
      setAccountNickname: (accountId, nickname) => set(
        (state) => {
          const updatedAccount = { ...state.config.accounts[accountId] }
          if (nickname === undefined) {
            delete updatedAccount.nickname
          } else {
            updatedAccount.nickname = nickname
          }
          return {
            config: {
              ...state.config,
              accounts: {
                ...state.config.accounts,
                [accountId]: updatedAccount
              }
            }
          }
        },
        false,
        'setAccountNickname'
      ),
      setTasklistSortType: (accountId, tasklistId, sortType) => set(
        (state) => ({
          config: {
            ...state.config,
            tasklists: {
              ...state.config.tasklists,
              [accountId]: {
                ...state.config.tasklists[accountId],
                [tasklistId]: {
                  ...state.config.tasklists[accountId]?.[tasklistId],
                  sortType
                }
              }
            }
          }
        }),
        false,
        'setTasklistSortType'
      ),
      cleanupAccount: (accountId) => set(
        (state) => {
          const newConfig = { ...state.config }
          delete newConfig.accounts[accountId]
          delete newConfig.tasklists[accountId]
          return { config: newConfig }
        },
        false,
        'cleanupAccount'
      ),
      cleanupTasklist: (accountId, tasklistId) => set(
        (state) => {
          const newConfig = { ...state.config }
          if (newConfig.tasklists[accountId]) {
            delete newConfig.tasklists[accountId][tasklistId]
            if (Object.keys(newConfig.tasklists[accountId]).length === 0) {
              delete newConfig.tasklists[accountId]
            }
          }
          
          if (newConfig.accounts[accountId]?.listOrder) {
            newConfig.accounts[accountId] = {
              ...newConfig.accounts[accountId],
              listOrder: newConfig.accounts[accountId].listOrder.filter(id => id !== tasklistId)
            }
          }
          
          return { config: newConfig }
        },
        false,
        'cleanupTasklist'
      ),
      markFeatureSeen: (featureKey) => set(
        (state) => {
          if (state.config.seenFeatures[featureKey]) return state
          return {
            config: {
              ...state.config,
              seenFeatures: {
                ...state.config.seenFeatures,
                [featureKey]: {
                  firstSeenAt: new Date().toISOString()
                }
              }
            }
          }
        },
        false,
        'markFeatureSeen'
      ),
      resetFeatureSeen: (featureKey) => set(
        (state) => {
          const newSeenFeatures = { ...state.config.seenFeatures }
          delete newSeenFeatures[featureKey]
          return {
            config: {
              ...state.config,
              seenFeatures: newSeenFeatures
            }
          }
        },
        false,
        'resetFeatureSeen'
      ),
      getConfig: () => get().config,
      reset: () => set({ config: defaultConfig, isLoaded: false }, false, 'reset')
    }),
    { name: 'ConfigStore', enabled: process.env.NODE_ENV !== 'production' }
  )
)

// Subscribe to config changes and trigger debounced sync
useConfigStore.subscribe(
  (state) => {
    debouncedFirestoreSync(state.config, state.isLoaded)
  }
)

export default useConfigStore

